/* Third party libraries */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  deleteClientWidget,
  getClientWidgetsList,
  getDashboardData,
  getWidgetsList,
  type PostClientWidgetInterface,
  type PostWidgetsListInterface,
  postClientWidget,
  postWidgetsList,
} from '@/api/analytics.ts';
import type { WidgetListInterface } from '@/components/Widgets/types';
import { notifyUser } from '@/store/Toast';
import type { DashboardType } from '@/types/Analytics.ts';
import { dashboardWidgetsDataQueryKey, dashboardWidgetsListQueryKey, invalidateDashboardWidgetsData } from './keys';

export { invalidateDashboardWidgetsData };

export const useDashboardData = ({
  onSuccess,
  onError,
  dashboard,
  widget,
}: {
  onSuccess?: (data: any) => void;
  onError?: (data: any) => void;
  dashboard: DashboardType;
  widget: string;
}) => {
  //Tanstack: version 5 query implementation
  return useQuery({
    queryKey: dashboardWidgetsDataQueryKey(dashboard),
    queryFn: () => getDashboardData(dashboard),
    select: (resData: any) => resData?.data?.find((d: any) => d?.slug === widget),
    onSuccess,
    onError,
    staleTime: 0,
    refetchOnMount: true,
  });
};

export const useDashboardWidgetsList = ({
  dashboard,
  onSuccess,
  onError,
}: {
  dashboard: DashboardType;
  onSuccess?: (data: any) => void;
  onError?: (data: any) => void;
}) => {
  const [isWidgetUsed, setIsWidgetUsed] = useState<{
    [key: string]: boolean;
  }>({});

  //Tanstack: version 5 query implementation
  const fetchDashboardWidgets = useQuery({
    queryKey: dashboardWidgetsListQueryKey(dashboard),
    queryFn: () => getWidgetsList(),
    select: (resData: any) => {
      return resData?.data?.filter((res: any) => res?.slug === dashboard);
    },
    onSuccess,
    onError,
    staleTime: 0,
    refetchOnMount: true,
  });
  //Widget data transformation to use isUsed key for dashboard widget rendering.
  //didn't use onSuccess and select props of useQuery because those can be useful
  //on other purposes
  const widgetList = fetchDashboardWidgets?.data?.[0]?.widgets;
  useEffect(() => {
    const transformedWidgetList = widgetList?.reduce(
      (acc: {}, data: WidgetListInterface) => ({
        ...acc,
        [data.slug]: data.isUsed,
      }),
      {}
    );
    setIsWidgetUsed(transformedWidgetList);
  }, [widgetList]);
  return { isWidgetUsed, fetchDashboardWidgets };
};

export const useDashboardWidgetListUpdate = ({ dashboard }: { dashboard: DashboardType }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation((data: PostWidgetsListInterface) => postWidgetsList(data), {
    onSuccess: () => {
      //Tanstack: Invalidate queries after mutation
      queryClient.invalidateQueries({ queryKey: dashboardWidgetsListQueryKey(dashboard) });
      queryClient.invalidateQueries({ queryKey: dashboardWidgetsDataQueryKey(dashboard) });
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Widget update successful',
          toastType: 'success',
        })
      );
    },
    onError: () => {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Widget update failed',
          toastType: 'failed',
        })
      );
    },
  });
};

export const useClientAccountWidgetList = ({
  accountId,
  widgetGroup,
  onSuccess,
  onError,
}: {
  accountId?: number;
  widgetGroup?: string;
  onSuccess?: (data: any) => void;
  onError?: (data: any) => void;
}) => {
  //Tanstack: version 5 query implementation
  return useQuery({
    queryKey: ['get-client-account-widgets', accountId],
    queryFn: () => getClientWidgetsList(accountId),
    select: (resData: any) => {
      if (widgetGroup) {
        return resData?.data?.find((wdg: any) => wdg.slug === widgetGroup);
      }
      return resData;
    },
    enabled: !!accountId,
    onSuccess,
    onError,
  });
};

export const useAssignWidgetToClientAccount = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation((data: PostClientWidgetInterface) => postClientWidget(data), {
    onSuccess: () => {
      //Tanstack: Invalidate queries after mutation
      queryClient.invalidateQueries({ queryKey: ['get-client-account-widgets'] });
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Widget assigned successfully',
          toastType: 'success',
        })
      );
    },
    onError: () => {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Widget assign failed',
          toastType: 'failed',
        })
      );
    },
  });
};

export const useUnassignWidgetToClientAccount = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation((data: PostClientWidgetInterface) => deleteClientWidget(data), {
    onSuccess: () => {
      //Tanstack: version 5 query implementation
      queryClient.invalidateQueries({ queryKey: ['get-client-account-widgets'] });
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Widget unassigned successfully',
          toastType: 'success',
        })
      );
    },
    onError: () => {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Widget already assigned to account.',
          toastType: 'failed',
        })
      );
    },
  });
};
